import { useMemo } from "react";
import { useProjectVarianceDrivers } from "@/hooks/useProjectVarianceDrivers";
import { useProjectChangeOrders } from "@/hooks/useProjectOrders";
import { useAlertThresholds, type ThresholdKey } from "@/hooks/useAlertThresholds";
import { useSovLineAlerts, type SovLineAlert } from "@/hooks/useSovLineAlerts";
import { useProjectContracts } from "@/hooks/useProjectContracts";

export type AlertSeverity = "info" | "warning" | "critical";
export type AlertCategory = "margin" | "override" | "unattributed" | "change_order" | "po_overrun" | "sov_summary";

export interface ProjectAlert {
  id: string;
  severity: AlertSeverity;
  category: AlertCategory;
  title: string;
  explanation: string;
  metric: number;
  threshold: number;
  /** Deterministic navigation target: tab value */
  linkTab?: string;
  /** Optional entity id for deep-linking */
  linkEntityId?: string;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

function severity(value: number, warning: number, critical: number): AlertSeverity {
  if (Math.abs(value) >= critical) return "critical";
  if (Math.abs(value) >= warning) return "warning";
  return "info";
}

export const useProjectAlerts = (projectId: string | undefined) => {
  const { data: varianceData, isLoading: vLoading } = useProjectVarianceDrivers(projectId);
  const { data: changeOrders } = useProjectChangeOrders(projectId);
  const { getThreshold } = useAlertThresholds();
  const { data: contracts } = useProjectContracts(projectId);
  const contractId = contracts?.[0]?.id;
  const { alertMap: sovAlertMap, isLoading: sovLoading } = useSovLineAlerts(contractId);

  const alerts = useMemo(() => {
    if (!varianceData) return [];

    const result: ProjectAlert[] = [];
    const s = varianceData.summary;
    const d = varianceData.drivers;

    // 1. Committed margin erosion
    const cmThresh = getThreshold("margin_drift_committed");
    if (cmThresh.active && s.committed_margin_variance < 0) {
      const drift = Math.abs(s.committed_margin_variance);
      if (drift >= cmThresh.warning) {
        result.push({
          id: "committed_margin_erosion",
          severity: severity(drift, cmThresh.warning, cmThresh.critical),
          category: "margin",
          title: "Committed margin erosion",
          explanation: `Committed margin dropped ${drift.toFixed(1)} pts below estimate (${s.estimated_margin_pct.toFixed(1)}% → ${s.committed_margin_pct.toFixed(1)}%).`,
          metric: drift,
          threshold: cmThresh.warning,
          linkTab: "overview",
        });
      }
    }

    // 2. Actual margin erosion
    const amThresh = getThreshold("margin_drift_actual");
    if (amThresh.active && s.actual_margin_variance < 0 && s.actual_cost > 0) {
      const drift = Math.abs(s.actual_margin_variance);
      if (drift >= amThresh.warning) {
        result.push({
          id: "actual_margin_erosion",
          severity: severity(drift, amThresh.warning, amThresh.critical),
          category: "margin",
          title: "Actual margin erosion",
          explanation: `Actual margin is ${drift.toFixed(1)} pts below estimate (${s.estimated_margin_pct.toFixed(1)}% → ${s.actual_margin_pct.toFixed(1)}%).`,
          metric: drift,
          threshold: amThresh.warning,
          linkTab: "overview",
        });
      }
    }

    // 3. Override exposure
    const oeThresh = getThreshold("override_exposure_pct");
    if (oeThresh.active && d.override_exposure > 0 && s.contract_value > 0) {
      const pct = (d.override_exposure / s.contract_value) * 100;
      if (pct >= oeThresh.warning) {
        result.push({
          id: "override_exposure",
          severity: severity(pct, oeThresh.warning, oeThresh.critical),
          category: "override",
          title: "Override exposure",
          explanation: `Override exposure is ${fmt(d.override_exposure)} (${pct.toFixed(1)}% of contract value) across ${d.override_line_count} line${d.override_line_count !== 1 ? "s" : ""}.`,
          metric: pct,
          threshold: oeThresh.warning,
          linkTab: "overview",
        });
      }
    }

    // 4. Unattributed expenses
    const ucThresh = getThreshold("unattributed_cost_pct");
    if (ucThresh.active && d.unattributed_actual > 0 && s.actual_cost > 0) {
      const pct = (d.unattributed_actual / s.actual_cost) * 100;
      if (pct >= ucThresh.warning) {
        result.push({
          id: "unattributed_expenses",
          severity: severity(pct, ucThresh.warning, ucThresh.critical),
          category: "unattributed",
          title: "Unattributed expenses",
          explanation: `${fmt(d.unattributed_actual)} in approved expenses have no SOV line attribution (${pct.toFixed(1)}% of actual cost).`,
          metric: pct,
          threshold: ucThresh.warning,
          linkTab: "expenses",
        });
      }
    }

    // 5. PO overruns
    if (d.po_overrun > 0) {
      result.push({
        id: "po_overrun",
        severity: "info",
        category: "po_overrun",
        title: "Material PO overruns",
        explanation: `Material PO overruns total ${fmt(d.po_overrun)} across ${d.po_overrun_line_count} SOV line${d.po_overrun_line_count !== 1 ? "s" : ""}.`,
        metric: d.po_overrun,
        threshold: 0,
        linkTab: "contract",
      });
    }

    // 6. Change order analysis
    const coThresh = getThreshold("co_margin_dilution");
    if (coThresh.active && changeOrders && changeOrders.length > 0 && s.contract_value > 0) {
      const approvedCOs = changeOrders.filter((co: any) => co.status === "approved");

      // Deductive COs
      const deductiveCOs = approvedCOs.filter((co: any) => Number(co.amount) < 0);
      if (deductiveCOs.length > 0) {
        const totalDeducted = deductiveCOs.reduce((sum: number, co: any) => sum + Math.abs(Number(co.amount)), 0);
        const linkedDeductives = deductiveCOs.filter((co: any) => co.work_order_id);
        const unlinkedDeductives = deductiveCOs.filter((co: any) => !co.work_order_id);

        if (linkedDeductives.length > 0) {
          const linkedTotal = linkedDeductives.reduce((sum: number, co: any) => sum + Math.abs(Number(co.amount)), 0);
          result.push({
            id: "deductive_co_linked",
            severity: "warning",
            category: "change_order",
            title: "Deductive change order risk",
            explanation: `Deductive CO${linkedDeductives.length > 1 ? "s" : ""} removed ${fmt(linkedTotal)} revenue from linked work orders where ${fmt(linkedTotal)} in cost is already committed.`,
            metric: linkedTotal,
            threshold: 0,
            linkTab: "change_orders",
          });
        }

        if (unlinkedDeductives.length > 0) {
          const unlinkedTotal = unlinkedDeductives.reduce((sum: number, co: any) => sum + Math.abs(Number(co.amount)), 0);
          result.push({
            id: "deductive_co_unlinked",
            severity: "info",
            category: "change_order",
            title: "Potential deductive CO risk",
            explanation: `${unlinkedDeductives.length} deductive CO${unlinkedDeductives.length > 1 ? "s" : ""} removed ${fmt(unlinkedTotal)} revenue. Cost impact could not be fully verified — potential risk.`,
            metric: unlinkedTotal,
            threshold: 0,
            linkTab: "change_orders",
          });
        }
      }

      // Additive CO margin dilution
      const additiveCOs = approvedCOs.filter((co: any) => Number(co.amount) > 0);
      if (additiveCOs.length > 0) {
        const totalAdded = additiveCOs.reduce((sum: number, co: any) => sum + Number(co.amount), 0);
        // Rough margin impact: if COs added revenue but committed cost grew faster
        const marginWithoutCOs = s.contract_value - totalAdded > 0
          ? ((s.contract_value - totalAdded - s.committed_cost) / (s.contract_value - totalAdded)) * 100
          : 0;
        const dilutionPts = marginWithoutCOs - s.committed_margin_pct;
        if (dilutionPts > coThresh.warning) {
          result.push({
            id: "co_margin_dilution",
            severity: severity(dilutionPts, coThresh.warning, coThresh.critical),
            category: "change_order",
            title: "Change order margin impact",
            explanation: `Change orders may have reduced project margin by ~${dilutionPts.toFixed(1)} pts.`,
            metric: dilutionPts,
            threshold: coThresh.warning,
            linkTab: "change_orders",
          });
        }
      }
    }

    // 7. SOV line alert summaries (noise control — group line-level alerts)
    if (sovAlertMap && sovAlertMap.size > 0) {
      const grouped: Record<string, { count: number; worstSeverity: AlertSeverity }> = {};

      sovAlertMap.forEach((lineAlerts) => {
        lineAlerts.forEach((a) => {
          if (!grouped[a.ruleId]) {
            grouped[a.ruleId] = { count: 0, worstSeverity: "info" as AlertSeverity as AlertSeverity };
          }
          grouped[a.ruleId].count++;
          if (a.severity === "critical") {
            grouped[a.ruleId].worstSeverity = "critical";
          } else if (a.severity === "warning" && grouped[a.ruleId].worstSeverity === "info") {
            grouped[a.ruleId].worstSeverity = "warning";
          }
        });
      });

      const ruleLabels: Record<string, string> = {
        committed_margin_floor: "committed margin below threshold",
        actual_margin_floor: "actual margin below threshold",
        override_present: "active rate overrides",
        actual_vs_estimate: "actual cost exceeding estimate",
      };

      Object.entries(grouped).forEach(([ruleId, { count, worstSeverity }]) => {
        if (count >= 2 || worstSeverity === "critical") {
          result.push({
            id: `sov_summary_${ruleId}`,
            severity: worstSeverity,
            category: "sov_summary",
            title: `${count} SOV lines flagged`,
            explanation: `${count} SOV lines have ${ruleLabels[ruleId] ?? ruleId}.`,
            metric: count,
            threshold: 0,
            linkTab: "contract",
          });
        }
      });
    }

    // Sort: critical first, then warning, then info. Within same severity, by metric desc.
    const severityOrder: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 };
    result.sort((a, b) => {
      const sd = severityOrder[a.severity] - severityOrder[b.severity];
      if (sd !== 0) return sd;
      return Math.abs(b.metric) - Math.abs(a.metric);
    });

    return result;
  }, [varianceData, changeOrders, sovAlertMap, getThreshold]);

  return {
    alerts,
    isLoading: vLoading || sovLoading,
    /** SOV line alert map for direct use in contract tab */
    sovAlertMap: sovAlertMap ?? new Map<string, SovLineAlert[]>(),
  };
};
